import { zodResolver } from '@hookform/resolvers/zod'
import { Eye, EyeOff, LoaderCircle } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { loginUser } from '@/app/conta/login/action'
import type { EnumProductType } from '@/types/enum-product-type'
import { Button } from '../ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form'
import { Input } from '../ui/input'
import { enrolAfterAuthentication } from './enrol-after-authentication'

interface EnrolmentDataType {
  product_id: string
  product_external_id?: number
  workload_value?: number
  workload_id: string
  period_id: string
}

interface SignInFormProps {
  email: string
  externalIdProduct: string | null
  referenceProduct: string
  productType: EnumProductType
  enrolmentData: EnrolmentDataType | null
}

export const signInFormSchema = z.object({
  email: z.string().min(1, { message: 'O campo email é obrigatorio' }),
  password: z.string().min(1, { message: 'O campo senha é obrigatorio' }),
})

type SignInFormType = z.infer<typeof signInFormSchema>

// TODO: Adicionar cpf também nesse login e fazer a validação das requestes individualmente
export default function SignInForm({
  email,
  enrolmentData,
  externalIdProduct,
  referenceProduct,
  productType,
}: SignInFormProps) {
  const [showPassword, setShowPassword] = useState(false)

  const form = useForm<SignInFormType>({
    resolver: zodResolver(signInFormSchema),
    mode: 'all',
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const {
    handleSubmit,
    control,
    setValue,
    formState: { isSubmitting },
  } = form

  async function onSubmit({ email, password }: SignInFormType) {
    try {
      const loginResult = await loginUser({ identifier: email, password })

      if (!loginResult?.data?.success) {
        toast.error('Não foi possível entrar na sua conta', {
          description: 'Verifique seus dados e tente novamente.',
          duration: 10_000,
        })
        return
      }

      if (enrolmentData) {
        await enrolAfterAuthentication({
          enrolmentData,
          externalIdProduct,
          productType,
          referenceProduct,
        })
      }
    } catch (error: unknown) {
      toast.error('Ops, ocorreu um erro, tente novamente mais tarde!', {
        duration: 10_000,
      })
    }
  }

  useEffect(() => {
    if (email) {
      setValue('email', email)
    }
  }, [email, setValue])

  const emailError = !!form.formState.errors.email?.message
  const passwordError = !!form.formState.errors.password?.message

  return (
    <Form {...form}>
      <form method="post"
        className="mt-6 bg-white"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
      >
        <FormField
          control={control}
          name="email"
          render={({ field }) => (
            <FormItem className="relative space-y-0">
              <FormLabel className="-top-2 absolute left-2.5 z-10 bg-white px-1 font-medium text-[#525252] text-xs">
                Seu E-mail
              </FormLabel>
              <FormControl>
                <Input
                  className="rounded text-sm placeholder:text-[#737373] data-[error=true]:border-red-500"
                  data-error={emailError}
                  disabled={isSubmitting}
                  placeholder="Digite seu e-mail"
                  {...field}
                />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="password"
          render={({ field }) => (
            <FormItem className="relative mt-4 space-y-0">
              <FormLabel className="-top-2 absolute left-2.5 z-10 bg-white px-1 font-medium text-[#525252] text-xs">
                Senha
              </FormLabel>
              <div className="relative">
                <FormControl>
                  <Input
                    className="rounded text-sm placeholder:text-[#737373] data-[error=true]:border-red-500"
                    data-error={passwordError}
                    disabled={isSubmitting}
                    placeholder="Digite sua senha"
                    type={showPassword ? 'text' : 'password'}
                    {...field}
                  />
                </FormControl>
                <Button
                  className="absolute top-0 right-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                  size="sm"
                  type="button"
                  variant="ghost"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span className="sr-only">
                    {showPassword ? 'Hide password' : 'Show password'}
                  </span>
                </Button>
              </div>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />

        <div className="mt-3.5">
          <Button
            className="h-10 rounded-1xl"
            disabled={isSubmitting}
            type="submit"
          >
            {isSubmitting ? (
              <LoaderCircle className="size-6 animate-spin" />
            ) : (
              'Avançar'
            )}
          </Button>
        </div>
      </form>
    </Form>
  )
}
